import { z } from "zod";

/**
 * Shared DataSource Link selection config.
 *
 * Used by every step that targets one or more DataSource Links — the
 * `observe_data_source_link` trigger and the blob-based DataSource Adapter steps
 * (`csv_datasource_adapter`, `fwf_datasource_adapter`). Centralising the shape
 * keeps the strategy choice, the UI selector, and the backend resolvers in sync.
 *
 * Two strategies:
 *   - 'static'         — target one specific DataSource Link by id.
 *   - 'matching_schema' — target every DataSource Link conforming to a chosen
 *     SchemaDefinition (the `import`-schema links for that schema). The trigger
 *     subscribes to CDC for all of their tables; the adapters emit all of them.
 */
export const dataSourceLinkSelectionSchema = z.object({
  dataSourceLinkStrategy: z
    .enum(["static", "matching_schema"])
    .optional()
    .describe(
      "How to resolve the target DataSource Link(s). " +
        "'static' selects one DataSource Link by id; " +
        "'matching_schema' targets every DataSource Link that conforms to the " +
        "selected SchemaDefinition.",
    ),
  dataSourceLinkId: z
    .string()
    .uuid()
    .optional()
    .describe(
      "DataSource Link id to use when dataSourceLinkStrategy is 'static'.",
    ),
  matchingSchemaId: z
    .string()
    .uuid()
    .optional()
    .describe(
      "SchemaDefinition id whose DataSource Links are targeted when " +
        "dataSourceLinkStrategy is 'matching_schema'. Also used to populate " +
        "column mapping options in the UI.",
    ),
});

export type DataSourceLinkSelection = z.infer<
  typeof dataSourceLinkSelectionSchema
>;
